import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import s from '@components/Questionnaire/form/List/List.module.scss';
import CopyButton from '@components/CopyButton';
import Copy from '@components/Icons/Copy';
import { Select } from '@components/DesignLibrary/Atoms';
import Button from '@components/DesignLibrary/Button';
import Modal from '@components/Modal';
import { Questionnaire } from '@typings/Questionnaire';
import uniq from 'lodash/uniq';
import removeNullish from '@helpers/removeNullish';
import { useWatch } from 'react-hook-form';
import { useQuestionnaireContext, useQuestionnaireFormContext } from '@/components/Questionnaire/QuestionnaireProvider';
import { getValidationRules } from '@helpers/validation/schema';
import { FormFieldProps } from '@components/Questionnaire/form/FormField/types';
import Header from '@components/DesignLibrary/Header';
import getDisplayValue from '@components/Questionnaire/form/List/getDisplayValue';
import Spacer from '@components/Spacer';
import DashboardContext from '@/layouts/DashboardLayout/DashboardContext';

type Props = {
  field: Questionnaire.FieldCommon;
  index: number;
  setIndex: (index?: number) => void;
  removeListItem?: (index: number) => void;
  fieldsAutofilling?: string[];
  linkSourceFieldKey: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: any;
  onSave?: (newValue: { _id: string }) => void;
  FormFieldComponent: FC<FormFieldProps>;
};

/* @deprecated - use EditListItemModalV2 instead, however this only supports the onSave version the moment */
const EditListItemModal: FC<Props> = ({
  field: initialField,
  index,
  setIndex,
  removeListItem,
  fieldsAutofilling,
  linkSourceFieldKey,
  control,
  onSave,
  FormFieldComponent,
}) => {
  const [field, setField] = useState(initialField);
  const [currentListSubsection, setCurrentListSubsection] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [referencedListData, setReferencedListData] = useState<any>(null);
  const [markingAsDuplicate, setMarkingAsDuplicate] = useState(false);

  const { fieldsByKey, isEnabled } = useQuestionnaireContext();
  const { formMethods, setValue } = useQuestionnaireFormContext();
  const { isInDashboard } = useContext(DashboardContext);

  const modalSubFields = useMemo(() => (field.listItemTemplates ?? []).map(subField => ({
    ...subField,
    computedKey: `${linkSourceFieldKey}[${index}].${subField.key}`,
    computedLinkedFields: fieldsByKey[subField.key]?.linkedFields?.filter(
      f => f.linkedFieldTemplateIndex === index,
    ),
    computedListItemTemplates: Object.values(fieldsByKey).filter(
      f => f.listFieldTemplateId === subField.id,
    ),
  })), [field.listItemTemplates, index]); // eslint-disable-line react-hooks/exhaustive-deps

  const modalFieldId = useWatch({
    control: formMethods.control,
    name: `${linkSourceFieldKey}[${index}]._id`,
  });

  const currentValues = useWatch({
    control: formMethods.control,
    name: linkSourceFieldKey,
  }) as Array<Record<string, unknown>>;

  const modalFieldAssociatedWithId = useWatch({
    control: formMethods.control,
    name: `${linkSourceFieldKey}[${index}]._associatedWithId`,
  });

  const [listResponses, setListResponses] = useState(formMethods.watch(linkSourceFieldKey) || []);

  const watcher = formMethods.watch(linkSourceFieldKey);

  useEffect(() => {
    setListResponses(watcher || []);
  },[watcher]);

  const listSubsections = useMemo(() => uniq(modalSubFields.map((subField) => subField.listSubsection).filter(removeNullish)), [modalSubFields]);

  const openModalFromReferencedList = (referencedField: Questionnaire.FieldList) : void => {
    setField(referencedField);
    if (referencedField.modalIndex !== undefined) {
      setIndex(referencedField.modalIndex);
    }
  };

  const handleReferencedListDataUpdate = useCallback<(data: {
    title: string;
    values: Array<{
      property: string;
      value: string;
    }> | null;
    onEditClick: () => void;
    onClose: () => void;
    type?: 'autofill' | 'ai-autofill' | 'questionnaire';
    isLoading?: boolean;
  } | null) => void>((newData) => {
      setReferencedListData(newData);
    }, []);

  useEffect(() => {
    setCurrentListSubsection(listSubsections[0] || null);
  }, [listSubsections]);

  const titleField = useMemo(() => modalSubFields.find((f) => f.isTitleField), [modalSubFields]);
  const subtitleFields = useMemo(() => modalSubFields.filter((f) => f.isSubtitleField), [modalSubFields]);

  return (
    <Modal
      title={`Edit ${field.label} item`}
      onClose={() => {
        setIndex(undefined);
        setReferencedListData(null);
      }}
      referencedListData={referencedListData}
      isWide
      hasForm={isEnabled}
      fixedHeight={listSubsections.length > 0 ? "80vh" : undefined}
      key={`${index}-${referencedListData?._id || ''}`}
    >
      <div className={s.modal}>
        {listSubsections.length > 0 && <Header
          title={formMethods.getValues()?.[linkSourceFieldKey]?.[index]?.[titleField?.key || ''] || field.label}
          description={
            <>
              <Spacer size={4} />
              <p className="caption">
                {subtitleFields.map((subtitleField, idx) => (
                  <React.Fragment key={subtitleField.id}>
                    <span style={{ color: 'var(--color-gray-75)' }}>{idx !== 0 ? ' · ' : ''}{subtitleField.label}: </span>
                    <span style={{ color: 'var(--color-gray-100)' }}>{getDisplayValue(subtitleField, formMethods.getValues()[linkSourceFieldKey][index][subtitleField.key])}</span>
                  </React.Fragment>
                ))}
              </p>
              <Spacer size={4} />
            </>
          }
          tabs={listSubsections.map((subsection) => ({
            label: subsection,
            onClick: () => setCurrentListSubsection(subsection === currentListSubsection ? null : subsection),
            selected: currentListSubsection === subsection,
          }))}
          className={s.modalHeader}
        />}
        {isEnabled && isInDashboard && (
          <div className={s.fieldId}>
            <div className={s.fieldIdData}>
              <div>
                <p className="caption">Field ID</p>
                <p className="caption">{modalFieldId}</p>
              </div>
              {listResponses.length > 1 && (
                <Button size='sm' variant='secondary' onClick={() => setMarkingAsDuplicate(prev => !prev)} className={modalFieldAssociatedWithId ? undefined : s.markAsDuplicate}>
                  {markingAsDuplicate ? 'Hide' : `Mark${modalFieldAssociatedWithId ? 'ed' : ''} as duplicate`}
                </Button>
              )}
              <CopyButton text={modalFieldId} className={s.copyIDButton}>
                <Copy size={16} /> Copy
              </CopyButton>
            </div>
            {markingAsDuplicate && (
              <Select
                compact
                label="This field is a duplicate of:"
                options={[
                  {
                    key: 'no',
                    label: 'None',
                  },
                  ...(listResponses as Array<{ _id: string; _associatedWithId: string | null }>)
                    .filter(subField => subField?._id !== listResponses[index]?._id && !subField._associatedWithId)
                    .map(subField => ({
                      key: subField._id,
                      label: subField._id,
                    })),
                ]}
                optional
                placeholder='None'
                value={modalFieldAssociatedWithId}
                onChange={(duplicateId) => {
                  const associatedWithId = duplicateId === 'no' ? null : duplicateId;
                  // @TODO - see if we can just do setValue(list[modalOpen]._associatedWithId, associatedWithId)
                  setValue({
                    key: linkSourceFieldKey,
                    value: currentValues.map((subField, idx) => ({
                      ...subField,
                      _associatedWithId: idx === index ? associatedWithId : subField._associatedWithId,
                    })),
                  });
                }}
              />
            )}
          </div>
        )}
        <div className={s.modalContent} {...listSubsections.length === 0 && { style: { minHeight: 'unset' } }}>
          {modalSubFields.filter((f) => currentListSubsection ? f.listSubsection === currentListSubsection : true).map(subField => (
            <FormFieldComponent
              key={subField.id}
              index={index}
              listField={field}
              field={{
                ...subField,
                key: subField.computedKey,
                linkedFields: subField.computedLinkedFields,
                listItemTemplates: subField.computedListItemTemplates,
              }}
              control={control}
              rules={getValidationRules(subField)}
              isEditing={listResponses[index]?._existsInDb}
              fieldsAutofilling={fieldsAutofilling}
              handleReferencedListDataUpdate={handleReferencedListDataUpdate}
              openModalFromReferencedList={openModalFromReferencedList}
            />
          ))}
        </div>

        {isEnabled && !field.disabled && (
          <div className={s.saveButton}>
            <Button
              variant="primary"
              size="md"
              onClick={() => {
                setIndex(undefined);
                setReferencedListData(null);
                const newValue = formMethods.getValues()[field.key]?.[index];
                if (onSave && newValue) {
                  onSave(newValue);
                }
              }}
            >
              Save
            </Button>
            {removeListItem && (
              <Button
                variant="destructive"
                size="md"
                onClick={() => {
                  if (window.confirm('Are you sure you want to remove this item?')) {
                    removeListItem(index);
                    setIndex(undefined);
                  }
                }}
              >
                Remove
              </Button>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default EditListItemModal;
